import {
  DesktopComputerIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  XIcon,
} from "@heroicons/react/outline";
import React, { useEffect, useMemo, useState } from "react";
import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  signUp,
} from "aws-amplify/auth";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../v2/Form/FormPhoneInput";
import { useNavigate } from "react-router-dom";
import { VIDEO_CALL_ADDON_ID, AUDIO_CALL_ADDON_ID } from "../../Constants";
import {
  addToCart,
  requestListService,
  requestListUserSubscription,
  requestPaymentMethods,
  useAuthState,
  useCheckoutDispatch,
  useCheckoutState,
  useConsultationDispatch,
  useConsultationState,
  useSettingsState,
  useSubscriptionDispatch,
  useSubscriptionState,
  useAuthDispatch,
  requestGetUser,
  checkOngoingGlobalConsultation,
  pay,
  verifyVoucher,
  clearCart,
  clearVoucher,
} from "../../Context";
import {
  classNames,
  convertToGoogleAnalyticsItem,
  getErrorMessagesString,
  transformAmplifyError,
  isConsultantOutOfHours,
  getConsultantNextAvailableTime,
  isGeneralConsultationOutOfHours,
  calculateExperience,
  trackAddToCart,
} from "../../Utils";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import {
  ArrowCircleRightIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
} from "@heroicons/react/solid";
import FormLabel from "../FormLabel";
import { Modal } from "../Modal";
import { get } from "aws-amplify/api";

const RESEND_DELAY = 60;

const defaultTheme = {
  primary: "#2563EB",
  primaryHover: "#1D4ED8",
  primaryLight: "#EFF6FF",
  primaryText: "#FFFFFF",
  background: "#F9FAFB",
  accent: "#BFDBFE",
  accentText: "#1D4ED8",
};

export const ConsultationFormWidget = ({
  categories = [],
  loading = false,
  consultant,
  consultantLoading,
  consultantError,
  withConsultantButton = true,
  withAddOns = true,
  guest = false,
  guestID = null,
  guestClient = null,
  embedMode = false,
  theme = defaultTheme,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { language } = useSettingsState();

  const checkoutDispatch = useCheckoutDispatch();
  const {
    loading: statusLoading,
    error,
    payError,
    paymentStatus,
    transactionId,
    consultationId,
    guestVoucher,
    guestVoucherLoading,
    guestVoucherError,
  } = useCheckoutState();

  const consultationDispatch = useConsultationDispatch();
  const { serviceLoading, services, serviceError } = useConsultationState();

  const dispatch = useAuthDispatch();
  const { user, loading: authLoading } = useAuthState();

  const isConsultant = user?.membership === "CONSULTANT";

  const subscriptionDispatch = useSubscriptionDispatch();
  const { userSubscriptions, loading: subscriptionLoading } =
    useSubscriptionState();

  const [category, setcategory] = useState("");
  const [description, setdescription] = useState("");
  const [phone, setphone] = useState("");
  const [email, setemail] = useState("");
  const [validatedEmail, setvalidatedEmail] = useState(false);
  const [emailLoading, setemailLoading] = useState(false);
  const [voucher, setvoucher] = useState("");
  const [code, setcode] = useState("");
  const [codeDeliveryDetails, setcodeDeliveryDetails] = useState({});
  const [guestAuthLoading, setguestAuthLoading] = useState(false);
  const [guestAuthError, setguestAuthError] = useState(null);
  const [callEnabled, setcallEnabled] = useState(false);
  // 'video' | 'audio' — which call add-on variant the call toggle buys.
  const [callType, setCallType] = useState("video");
  const [zoomEnabled, setzoomEnabled] = useState(false);
  const [consultationTotal, setconsultationTotal] = useState(0);
  const [discount, setdiscount] = useState(0);
  const [callTotal, setcallTotal] = useState(0);
  const [subtotal, setsubtotal] = useState(0);
  const [errorMessage, seterrorMessage] = useState("");
  const [showTermModal, setshowTermModal] = useState(false);
  const [showGuestModal, setshowGuestModal] = useState(false);
  const [guestModalType, setguestModalType] = useState("");
  const [guestVoucherApplied, setguestVoucherApplied] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showConfirmModal, setshowConfirmModal] = useState(false);
  const [confirmModalMessage, setconfirmModalMessage] = useState("");
  const [ongoingGlobalConsultation, setongoingGlobalConsultation] =
    useState(null);

  const videoCallAvailable =
    paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.available &&
    (!consultant?.id || (consultant?.acceptCall && consultant?.active));
  // Saldo path only (mirrors checkout): a second global-balance consultation
  // would let two calls burn the same balance concurrently.
  const videoCallBlocked =
    user.callBalance >= 60 && !!ongoingGlobalConsultation;
  const videoCallPrice =
    consultant?.callPrice || paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.price;
  const videoCallDuration =
    paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.duration || 0;
  // Strike-through anchors come from each product's own `original`; hidden when
  // a consultant override is set (no meaningful "original" for custom pricing).
  const videoCallOriginal = consultant?.callPrice
    ? null
    : paymentStatus?.prices?.[VIDEO_CALL_ADDON_ID]?.original;

  // Audio-only variant: same consultant acceptCall gate as video, plus its own
  // availability flag in prices.
  const audioCallAvailable =
    videoCallAvailable &&
    paymentStatus?.prices?.[AUDIO_CALL_ADDON_ID]?.available;
  const audioCallPrice =
    consultant?.audioPrice || paymentStatus?.prices?.[AUDIO_CALL_ADDON_ID]?.price;
  const audioCallOriginal = consultant?.audioPrice
    ? null
    : paymentStatus?.prices?.[AUDIO_CALL_ADDON_ID]?.original;
  const audioCallDuration =
    paymentStatus?.prices?.[AUDIO_CALL_ADDON_ID]?.duration || 0;

  const zoomCallAvailable =
    paymentStatus?.prices?.["zoom-addon"]?.available &&
    (!consultant?.id || (consultant?.acceptZoom && consultant?.active));
  const zoomCallPrice =
    consultant?.zoomPrice || paymentStatus?.prices?.["zoom-addon"]?.price;
  const zoomCallOriginal = consultant?.zoomPrice
    ? null
    : paymentStatus?.prices?.["zoom-addon"]?.original;
  const zoomCallDuration = paymentStatus?.prices?.["zoom-addon"]?.duration || 0;

  const consultationDuration =
    paymentStatus?.prices?.["online-consultation"]?.duration || 0;

  const consultationTerms =
    language === "id"
      ? paymentStatus?.prices?.["online-consultation"]?.terms?.id
      : paymentStatus?.prices?.["online-consultation"]?.terms?.en;

  const consultationData = paymentStatus?.prices?.["online-consultation"];

  const experienceYear = calculateExperience(consultant);

  const isOutOfHours = useMemo(() => {
    return (
      (consultant?.active && isConsultantOutOfHours(consultant)) ||
      (!consultant?.id && isGeneralConsultationOutOfHours(consultationData))
    );
  }, [consultant, consultationData]);

  // user.callBalance decides whether video call uses the saldo (useGlobalBalance)
  // or charges the add-on, but the auth user is only fetched at login — saldo
  // credited since then (membership purchase, leftover transfer) would be
  // invisible here and the user would be charged instead. Refresh on mount.
  useEffect(() => {
    if (user?.id && !guest) {
      requestGetUser(dispatch, { sub: user.id });
      // Only one ONGOING consultation may draw on the global balance at a
      // time (checkout rejects a second one) — warn before checkout. A failed
      // check stays null: no false block, the server guard still catches it.
      if (!isConsultant) {
        checkOngoingGlobalConsultation(user.id)
          .then(setongoingGlobalConsultation)
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, guest]);

  // The ongoing-consultation check is async — if it resolves after the user
  // already toggled Video Call on, switch it back off (the toggle is disabled
  // from then on).
  useEffect(() => {
    if (videoCallBlocked && callEnabled) {
      setcallEnabled(false);
    }
  }, [videoCallBlocked, callEnabled]);

  useEffect(() => {
    if (user.phone) {
      setphone(user.phone);
    }
  }, [user.phone]);

  useEffect(() => {
    if (user.email) {
      setemail(user.email);
    }
  }, [user.email]);

  useEffect(() => {
    if (categories.length === 0) {
      requestListService(consultationDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  useEffect(() => {
    if (!paymentStatus.prices) {
      if (guest) {
        requestPaymentMethods(checkoutDispatch, {
          productType: "consultation-trial",
        });
      } else {
        requestPaymentMethods(checkoutDispatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus.prices, guest]);

  useEffect(() => {
    if (guestVoucher?.id === voucher) {
      setguestVoucherApplied(true);
    } else {
      setguestVoucherApplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestVoucher, voucher]);

  useEffect(() => {
    if (consultationId || transactionId) {
      onGuestConsultationSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, dispatch, transactionId, consultationId]);

  useEffect(() => {
    const getGuestAccount = async (guestID, guestClient) => {
      try {
        setemailLoading(true);
        const user = await getCurrentUser();
        if (user.username) {
          setvalidatedEmail(false);
        }
      } catch (error) {
        try {
          let res = await get({
            apiName: "user",
            path: `/guest/${guestClient}/${guestID}`,
          }).response;

          res = await res.body.json();

          const { success, data } = res;
          if (success) {
            const { email: linkedEmail } = data;
            setemail(linkedEmail);
            setvalidatedEmail(true);
          } else {
            setvalidatedEmail(false);
          }
        } catch (apiError) {
          setvalidatedEmail(false);
        }
      } finally {
        setemailLoading(false);
      }
    };

    if (guestID && guestClient && !user?.id) {
      getGuestAccount(guestID, guestClient);
    } else {
      setvalidatedEmail(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, guestID, guestClient]);

  useEffect(() => {
    if (!error) {
      seterrorMessage("");
    } else {
      let errorBody =
        "Consultation services are currently unavailable. Please try again later.";
      if (error.message) {
        errorBody = error.message;
      }
      seterrorMessage(errorBody);
    }
  }, [error]);

  useEffect(() => {
    if (!payError) {
      seterrorMessage("");
    } else {
      let errorBody =
        "Consultation services are currently unavailable. Please try again later.";
      const parsedErrorMessage = getErrorMessagesString(payError);
      if (parsedErrorMessage) {
        errorBody = parsedErrorMessage;
      }
      seterrorMessage(errorBody);
    }
  }, [payError]);

  useEffect(() => {
    if (!guestVoucherError) {
      seterrorMessage("");
    } else {
      let errorBody = "The voucher code is invalid. Please try again.";
      const parsedErrorMessage = getErrorMessagesString(guestVoucherError);
      if (parsedErrorMessage) {
        errorBody = parsedErrorMessage;
      }
      seterrorMessage(errorBody);
    }
  }, [guestVoucherError]);

  useEffect(() => {
    if (!consultantError) {
      seterrorMessage("");
    } else {
      let errorBody =
        "Consultation services are currently unavailable. Please try again later.";
      if (consultantError.message) {
        errorBody = consultantError.message;
      }
      seterrorMessage(errorBody);
    }
  }, [consultantError]);

  useEffect(() => {
    if (!serviceError) {
      seterrorMessage("");
    } else {
      let errorBody =
        "Consultation services are currently unavailable. Please try again later.";
      if (serviceError.message) {
        errorBody = serviceError.message;
      }
      if (serviceError.errors) {
        const errorMessages = transformAmplifyError(serviceError.errors);
        errorBody = errorMessages.join(" ");
      }
      seterrorMessage(errorBody);
    }
  }, [serviceError]);

  useEffect(() => {
    let consultation = 0;
    let call = 0;
    if (
      paymentStatus?.prices &&
      paymentStatus?.prices["online-consultation"] &&
      paymentStatus?.prices[VIDEO_CALL_ADDON_ID] &&
      paymentStatus?.prices["zoom-addon"]
    ) {
      if (callEnabled && videoCallAvailable) {
        if (user.callBalance >= 60) {
          call = 0;
        } else if (callType === "audio" && audioCallAvailable) {
          call = audioCallPrice;
        } else if (consultant?.callPrice) {
          call = consultant.callPrice;
        } else {
          call = paymentStatus?.prices[VIDEO_CALL_ADDON_ID]?.price;
        }
      } else if (zoomEnabled && zoomCallAvailable) {
        if (consultant?.zoomPrice) {
          call = consultant.zoomPrice;
        } else {
          call = paymentStatus?.prices["zoom-addon"]?.price;
        }
      }

      if (consultant?.consultationPrice) {
        consultation = consultant.consultationPrice;
      } else {
        consultation = paymentStatus?.prices["online-consultation"].price;
      }
    }
    setconsultationTotal(consultation);
    setcallTotal(call);
  }, [
    callEnabled,
    callType,
    audioCallAvailable,
    audioCallPrice,
    paymentStatus,
    user,
    consultant,
    videoCallAvailable,
    zoomCallAvailable,
    zoomEnabled,
  ]);

  // Audio choice only exists while the audio add-on is purchasable.
  useEffect(() => {
    if (callType === "audio" && !audioCallAvailable) {
      setCallType("video");
    }
  }, [callType, audioCallAvailable]);

  useEffect(() => {
    let total = consultationTotal + callTotal - discount;
    if (total < 0) {
      total = 0;
    }
    setsubtotal(total);
  }, [callTotal, discount, consultationTotal]);

  useEffect(() => {
    if (consultant?.services?.items?.length > 0) {
      setcategory(consultant.services.items[0].service.id);
    } else {
      setcategory("general");
    }
  }, [consultant?.services?.items]);

  useEffect(() => {
    if (user.id) {
      requestListUserSubscription(subscriptionDispatch, { userID: user.id });
    } else {
      seterrorMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    if (userSubscriptions.length > 0) {
      const currentDate = new Date();
      let availableDiscount = 0;
      userSubscriptions.forEach((subscription) => {
        const expiredAt = new Date(subscription.expiredAt);
        if (expiredAt > currentDate) {
          if (subscription.voucher?.category === "consultation") {
            let currentDiscount = 0;
            const { total, type } = subscription.voucher;
            if (type === "PERCENTAGE") {
              currentDiscount = (consultationTotal * total) / 100;
            }
            if (type === "FIXED") {
              currentDiscount = total;
            }
            if (currentDiscount > availableDiscount) {
              availableDiscount = currentDiscount;
            }
          }
        }
      });
      setdiscount(availableDiscount);
    } else if (guestVoucher.id && guestVoucherApplied) {
      let availableDiscount = 0;
      const { total, type } = guestVoucher;
      if (type === "PERCENTAGE") {
        availableDiscount = (consultationTotal * total) / 100;
      }
      if (type === "FIXED") {
        availableDiscount = total;
      }
      setdiscount(availableDiscount);
    }
  }, [userSubscriptions, consultationTotal, guestVoucher, guestVoucherApplied]);

  useEffect(() => {
    const storedEndTime = localStorage.getItem("resendCodeEndTime");
    if (storedEndTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      const remainingTime = storedEndTime - currentTime;

      if (remainingTime > 0) {
        setTimer(remainingTime);
      }
    }
  }, []);

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (
      guestClient?.toLowerCase() === "hibank" &&
      !guestVoucherApplied &&
      !guestVoucher?.id
    ) {
      setvoucher("HIBANK");
      verifyVoucher(checkoutDispatch, {
        code: "HIBANK",
        productType: "consultation-trial",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestClient, checkoutDispatch]);

  function applyTimer() {
    setTimer(RESEND_DELAY);
    const endTime = Math.floor(Date.now() / 1000) + RESEND_DELAY;
    localStorage.setItem("resendCodeEndTime", endTime);
  }

  function resetTimer() {
    setTimer(0);
    localStorage.removeItem("resendCodeEndTime");
  }

  const guestRegister = async () => {
    try {
      setguestAuthLoading(true);
      setguestAuthError(null);
      setcodeDeliveryDetails({});

      const res = await signUp({
        username: email,
        options: {
          userAttributes: {
            email,
          },
          autoSignIn: {
            authFlowType: "USER_AUTH",
          },
        },
      });

      if (res.nextStep.codeDeliveryDetails) {
        setcodeDeliveryDetails(res.nextStep.codeDeliveryDetails);
        applyTimer();
        setguestModalType("CONFIRM_SIGN_UP");
        setshowGuestModal(true);
      }
    } catch (error) {
      if (error.name === "UsernameExistsException") {
        guestSignIn();
      } else if (error.name === "UserNotConfirmedException") {
        setguestModalType("CONFIRM_SIGN_UP");
        setshowGuestModal(true);
      } else {
        setguestAuthError(error);
      }
    } finally {
      setguestAuthLoading(false);
    }
  };

  const guestSignIn = async () => {
    try {
      setguestAuthLoading(true);
      setguestAuthError(null);
      setcodeDeliveryDetails({});

      const res = await signIn({
        username: email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      });

      setshowGuestModal(false);

      if (res.isSignedIn) {
        setshowGuestModal(false);
        resetModalForm();
        resetTimer();
        onPay();
      }

      if (res.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        setcodeDeliveryDetails(res.nextStep.codeDeliveryDetails);
        applyTimer();
        setguestModalType("CONFIRM_SIGN_IN");
        setshowGuestModal(true);
      } else if (res.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        setguestModalType("CONFIRM_SIGN_UP");
        setshowGuestModal(true);
      }
    } catch (error) {
      if (error.name === "UserNotFoundException") {
        guestRegister();
      } else if (error.name === "UserNotConfirmedException") {
        setguestModalType("CONFIRM_SIGN_UP");
        setshowGuestModal(true);
      } else {
        setguestAuthError(error);
      }
    } finally {
      setguestAuthLoading(false);
    }
  };

  const guestConfirm = async () => {
    try {
      setguestAuthLoading(true);
      setguestAuthError(null);
      setcodeDeliveryDetails({});

      const res = await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      if (res.nextStep.signUpStep === "DONE") {
        setshowGuestModal(false);
        resetModalForm();
        resetTimer();
        guestSignIn();
      }

      if (res.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        const res = await autoSignIn();
        if (res.isSignedIn) {
          setshowGuestModal(false);
          resetModalForm();
          resetTimer();
          onPay();
        }
      }
    } catch (error) {
      setguestAuthError(error);
    } finally {
      setguestAuthLoading(false);
    }
  };

  const guestSignInConfirm = async () => {
    try {
      setguestAuthLoading(true);
      setguestAuthError(null);
      const res = await confirmSignIn({
        challengeResponse: code,
      });
      if (res.isSignedIn) {
        setshowGuestModal(false);
        resetModalForm();
        resetTimer();
        onPay();
      }
    } catch (error) {
      setguestAuthError(error);
    } finally {
      setguestAuthLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setguestAuthLoading(true);
      setcodeDeliveryDetails({});

      const res = await resendSignUpCode({ username: email });
      if (res) {
        setcodeDeliveryDetails(res);
      }

      applyTimer();
    } catch (error) {
      setguestAuthError(error);
    } finally {
      setguestAuthLoading(false);
    }
  };

  const handleResendSignInCode = async () => {
    try {
      setguestAuthLoading(true);
      setcodeDeliveryDetails({});
      const res = await signIn({
        username: email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      });
      if (res.nextStep?.codeDeliveryDetails) {
        setcodeDeliveryDetails(res.nextStep.codeDeliveryDetails);
      }
      applyTimer();
    } catch (error) {
      if (error.name === "UserNotConfirmedException") {
        setguestModalType("CONFIRM_SIGN_UP");
        setshowGuestModal(true);
      } else {
        setguestAuthError(error);
      }
    } finally {
      setguestAuthLoading(false);
    }
  };

  const onPay = () => {
    const products = [];

    // get name from services
    const service = services.find((service) => service.id === category);

    const newConsultationPricing = consultant?.consultationPrice
      ? consultant.consultationPrice
      : paymentStatus?.prices["online-consultation"]?.price;

    const consultationDetail = {
      online: true,
      categoryID: category,
      categoryName: !service ? category : service.name,
      description,
      phone,
      callEnabled: false,
      zoomEnabled: false,
      useGlobalBalance: false,
    };

    products.push({
      id: "consultation-trial",
      type: "consultation",
      detail: consultationDetail,
      name: t("Online Tax Consultation"),
      price: newConsultationPricing,
      quantity: 1,
    });

    const payload = {
      products,
      paymentMethod: "voucher",
      voucher,
    };

    pay(checkoutDispatch, payload);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Validate form first
    const validation = validateForm();
    if (!validation.isValid) {
      seterrorMessage(validation.message);
      return;
    }

    // If form is valid and out of hours, show confirmation modal
    if (isOutOfHours) {
      setconfirmModalMessage(t("confirmConsultationMessage"));
      setshowConfirmModal(true);
    } else {
      // If not out of hours, proceed directly
      proceedWithConsultation();
    }
  };

  const onGuestConsultationSuccess = () => {
    clearCart(checkoutDispatch);
    if (consultationId) {
      if (embedMode) {
        const clientParam = guestClient
          ? `?client=${encodeURIComponent(guestClient)}`
          : "";
        navigate(`/embed/consultation/redirect/${consultationId}${clientParam}`);
      } else {
        navigate(`/consultation/redirect/${consultationId}`);
      }
      return;
    }
    if (transactionId) {
      navigate(`/transaction/${transactionId}`);
      return;
    }
  };

  const resetModalForm = () => {
    setcode("");
    setcodeDeliveryDetails({});
    setguestAuthError(null);
  };

  const resetVoucher = () => {
    clearVoucher(checkoutDispatch);
    setvoucher("");
    setguestVoucherApplied(false);
  };

  const formatConsultationDuration = (duration) => {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);

    if (hours > 0) {
      return minutes > 0
        ? `${hours}${t("h")} ${minutes}${t("mnt")}`
        : `${hours}${t("h")}`;
    } else {
      return `${minutes}${t("mnt")}`;
    }
  };

  // Format an add-on duration (in seconds) as whole hours/minutes/seconds,
  // skipping zero parts, to avoid showing decimals like "1.6666 Menit".
  const formatAddonDuration = (durationSeconds) => {
    const total = Math.max(0, Math.floor(durationSeconds || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${t("Hours")}`);
    if (minutes > 0) parts.push(`${minutes} ${t("Minutes")}`);
    if (seconds > 0) parts.push(`${seconds} ${t("Seconds")}`);
    if (parts.length === 0) parts.push(`0 ${t("Minutes")}`);
    return parts.join(" ");
  };

  const validateForm = () => {
    // Check required fields based on form state
    if (!category) {
      return { isValid: false, message: t("pleaseSelectCategory") };
    }

    // For guest users, email is required if not validated
    if (guest && !user.id && !validatedEmail && !email) {
      return { isValid: false, message: t("pleaseEnterValidEmail") };
    }

    // For guest users, voucher is required
    if (guest && !guestVoucherApplied) {
      return { isValid: false, message: t("pleaseEnterVoucherCode") };
    }

    // Check if consultant is active when selected
    if (consultant?.id && !consultant?.active) {
      return { isValid: false, message: t("pleaseSelectConsultant") };
    }

    // Saldo path only (mirrors checkout): a second global-balance consultation
    // would let two calls burn the same balance concurrently.
    if (
      callEnabled &&
      videoCallAvailable &&
      user.callBalance >= 60 &&
      ongoingGlobalConsultation
    ) {
      return { isValid: false, message: t("ongoingGlobalCallWarning") };
    }

    return { isValid: true, message: "" };
  };

  const proceedWithConsultation = () => {
    // Close the confirmation modal
    setshowConfirmModal(false);

    // Original handleSubmit logic
    if (guest && user?.id) {
      onPay();
      return;
    }

    if (guestModalType) {
      setshowGuestModal(true);
      return;
    }

    if (guest) {
      guestSignIn();
      return;
    }

    const buyAudio = callType === "audio" && audioCallAvailable;
    const newCallPricing = buyAudio
      ? audioCallPrice
      : consultant?.callPrice
        ? consultant.callPrice
        : paymentStatus?.prices[VIDEO_CALL_ADDON_ID]?.price;
    const newZoomPricing = consultant?.zoomPrice
      ? consultant.zoomPrice
      : paymentStatus?.prices["zoom-addon"]?.price;
    const newConsultationPricing = consultant?.consultationPrice
      ? consultant.consultationPrice
      : paymentStatus?.prices["online-consultation"]?.price;

    // get name from services
    const service = services.find((service) => service.id === category);

    const consultationDetail = {
      online: true,
      categoryID: category,
      categoryName: !service ? category : service.name,
      description,
      phone,
      callEnabled: false,
      zoomEnabled: false,
      useGlobalBalance: false,
    };

    if (consultant?.id) {
      consultationDetail.consultantID = consultant.id;
    }

    let products = [];

    if (callEnabled && videoCallAvailable) {
      // processTransaction reads callType from the detail on the saldo path
      // (no add-on product there) and infers it from the product id otherwise.
      consultationDetail.callType = buyAudio ? "audio" : "video";
      if (user.callBalance >= 60) {
        consultationDetail.useGlobalBalance = true;
        consultationDetail.globalBalance = user.callBalance;
      } else {
        consultationDetail.callEnabled = true;
        products.push({
          id: buyAudio ? AUDIO_CALL_ADDON_ID : VIDEO_CALL_ADDON_ID,
          type: "consultation",
          detail: consultationDetail,
          name: buyAudio ? t("Audio Call Add-On") : t("Video Call Add-On"),
          price: newCallPricing,
          quantity: 1,
        });
      }
    }
    if (zoomEnabled && zoomCallAvailable) {
      consultationDetail.zoomEnabled = true;
      products.push({
        id: "zoom-addon",
        type: "consultation",
        detail: consultationDetail,
        name: t("Zoom Call Add-On"),
        price: newZoomPricing,
        quantity: 1,
      });
    }

    products.push({
      id: "online-consultation",
      type: "consultation",
      detail: consultationDetail,
      name: t("Online Tax Consultation"),
      price: newConsultationPricing,
      quantity: 1,
    });

    const analyticsParams = {
      currency: "IDR",
      value: products.reduce((total, product) => {
        return total + product.price * product.quantity;
      }, 0),
      items: products.map((product) => convertToGoogleAnalyticsItem(product)),
    };
    logEvent(analytics, "add_to_cart", analyticsParams);
    trackAddToCart({ products });

    addToCart(checkoutDispatch, {
      products,
    });

    navigate("/checkout");
  };

  const renderTermModal = () => {
    return (
      <Modal
        title="Consultation Duration Terms"
        show={showTermModal}
        onClose={() => setshowTermModal(false)}
      >
        <div className="flex flex-col p-4 gap-4">
          {consultationTerms?.map((term) => (
            <div className="flex gap-2" key={term.id}>
              <span>{term.id}.</span>
              <p>{term.term}</p>
            </div>
          ))}
        </div>
      </Modal>
    );
  };

  const renderGuestModal = () => {
    if (guestModalType === "CONFIRM_SIGN_IN") {
      return (
        <Modal
          title="Log in verification"
          show={showGuestModal}
          onClose={() => {
            setshowGuestModal(false);
            resetModalForm();
          }}
          footer={
            <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
              <Button
                disabled={code.length < 6 || authLoading || guestAuthLoading}
                loading={authLoading || guestAuthLoading}
                onClick={(e) => {
                  e.preventDefault();
                  guestSignInConfirm();
                }}
                className="w-24 shrink-0"
                title="Continue"
              />
            </div>
          }
        >
          <div className="flex flex-col p-4 gap-2">
            {guestAuthError?.message || guestAuthError?.errors ? (
              <Alert
                type="error"
                message={guestAuthError?.message}
                errors={guestAuthError?.errors}
              />
            ) : null}
            {codeDeliveryDetails.deliveryMedium && (
              <Alert
                type="info"
                message={`${t("Verification code has been sent using")} ${
                  codeDeliveryDetails.deliveryMedium
                } ${t("to")} ${codeDeliveryDetails.destination}`}
              />
            )}
            <div>
              <div className="flex justify-between">
                <FormLabel
                  htmlFor="code"
                  label={t("Verification Code")}
                  required
                />
                <button
                  disabled={timer > 0 || guestAuthLoading}
                  onClick={handleResendSignInCode}
                >
                  <span
                    className="text-sm"
                    style={{ color: timer > 0 ? "#D1D5DB" : theme.primary }}
                  >
                    {t("Resend code")}
                    {timer > 0 && ` (${timer})`}
                  </span>
                </button>
              </div>
              <div className="relative mt-1">
                <input
                  type="number"
                  id="code"
                  className="w-full p-4 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0"
                  placeholder={t("Enter verification code")}
                  value={code}
                  onChange={(event) => {
                    setcode(event.target.value);
                  }}
                  required
                />
              </div>
            </div>
          </div>
        </Modal>
      );
    }

    if (guestModalType === "CONFIRM_SIGN_UP") {
      return (
        <Modal
          title="Confirm your account"
          show={showGuestModal}
          onClose={() => {
            setshowGuestModal(false);
            resetModalForm();
          }}
          footer={
            <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
              <Button
                disabled={code.length < 6 || authLoading || guestAuthLoading}
                loading={authLoading || guestAuthLoading}
                onClick={(e) => {
                  e.preventDefault();
                  guestConfirm();
                }}
                className="w-24 shrink-0"
                title="Continue"
              />
            </div>
          }
        >
          <div className="flex flex-col p-4 gap-2">
            {guestAuthError?.message || guestAuthError?.errors ? (
              <Alert
                type="error"
                message={guestAuthError?.message}
                errors={guestAuthError?.errors}
              />
            ) : null}
            {codeDeliveryDetails.deliveryMedium && (
              <Alert
                type="info"
                message={`${t("Verification code has been sent using")} ${
                  codeDeliveryDetails.deliveryMedium
                } ${t("to")} ${codeDeliveryDetails.destination}`}
              />
            )}
            <div>
              <div className="flex justify-between">
                <FormLabel
                  htmlFor="code"
                  label={t("Verification Code")}
                  required
                />
                <button disabled={timer > 0} onClick={handleResendCode}>
                  <span
                    className="text-sm"
                    style={{ color: timer > 0 ? "#D1D5DB" : theme.primary }}
                  >
                    {t("Resend code")}
                    {timer > 0 && ` (${timer})`}
                  </span>
                </button>
              </div>
              <div className="relative mt-1">
                <input
                  type="number"
                  id="code"
                  className="w-full p-4 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0"
                  placeholder={t("Enter verification code")}
                  value={code}
                  onChange={(event) => {
                    setcode(event.target.value);
                  }}
                  required
                />
              </div>
            </div>
          </div>
        </Modal>
      );
    }

    return null;
  };

  const renderConfirmModal = () => {
    return (
      <Modal
        show={showConfirmModal}
        onClose={() => setshowConfirmModal(false)}
        title={t("confirmConsultation")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              onClick={() => setshowConfirmModal(false)}
            >
              {t("common.cancel")}
            </button>
            <Button
              onClick={proceedWithConsultation}
              className="w-24 shrink-0"
              title={t("common.continue")}
            />
          </div>
        }
      >
        <div className="flex flex-col p-4 gap-4">
          {isOutOfHours && (
            <div className="flex items-center justify-between py-2 px-3 bg-amber-100 text-gray-700 rounded-md">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-amber-600">
                  {t("consultationOutsideWorkingHours")}
                </span>
                <span className="text-sm text-amber-800">
                  {t("consultationOutsideWorkingHoursMessage")}
                  {(() => {
                    const nextAvailableTime = consultant?.active
                      ? getConsultantNextAvailableTime(consultant, t)
                      : null;

                    return nextAvailableTime ? (
                      <>
                        <br />
                        <br />
                        {t("nextAvailableTime", { time: nextAvailableTime })}
                      </>
                    ) : null;
                  })()}
                </span>
              </div>
            </div>
          )}
          <div className="text-sm text-gray-600">{confirmModalMessage}</div>
        </div>
      </Modal>
    );
  };

  const isDisabled =
    loading ||
    guestAuthLoading ||
    subscriptionLoading ||
    statusLoading ||
    emailLoading ||
    error ||
    (!consultant?.active && consultant?.id) ||
    (guest && !guestVoucherApplied) ||
    (guest && !email) ||
    isConsultant;

  return (
    <form action="#" onSubmit={handleSubmit}>
      <div className="flex flex-col p-4 gap-2">
        {renderTermModal()}
        {renderGuestModal()}
        {renderConfirmModal()}
        {isConsultant && (
          <Alert
            title={t("Consultant Account")}
            type="info"
            message={t(
              "Consultants cannot order services. This form is for viewing purposes only.",
            )}
          />
        )}
        {categories.length > 0 && (
          <div>
            <FormLabel htmlFor="category" label={t("Category")} required />
            <select
              id="category"
              name="category"
              onChange={(event) => {
                setcategory(event.target.value);
              }}
              value={category}
              disabled={isConsultant}
              className={classNames(
                "mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-0 text-sm",
                isConsultant
                  ? "bg-gray-100 cursor-not-allowed opacity-60"
                  : "bg-white",
              )}
            >
              {(consultant?.services?.items?.length > 0
                ? categories.filter((service) =>
                    consultant.services?.items.some(
                      (item) => item.service.id === service.id,
                    ),
                  )
                : categories
              ).map((filteredService) => (
                <option key={filteredService.id} value={filteredService.id}>
                  {t(filteredService.name)}
                </option>
              ))}
            </select>
          </div>
        )}

        {categories.length === 0 && (
          <div>
            <FormLabel htmlFor="category" label={t("Category")} required />
            {serviceLoading ? (
              <div className="flex animate-pulse mt-1 py-2 px-3 border border-gray-300 rounded-md">
                <div className="h-6 w-full rounded-md bg-gray-300"></div>
              </div>
            ) : (
              <select
                id="category"
                name="category"
                onChange={(event) => {
                  setcategory(event.target.value);
                }}
                value={category}
                disabled={isConsultant}
                className={classNames(
                  "mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500 text-sm",
                  isConsultant
                    ? "bg-gray-100 cursor-not-allowed opacity-60"
                    : "bg-white",
                )}
              >
                {(consultant?.services?.items?.length > 0
                  ? services.filter((service) =>
                      consultant.services?.items.some(
                        (item) => item.service.id === service.id,
                      ),
                    )
                  : services
                ).map((filteredService) => (
                  <option key={filteredService.id} value={filteredService.id}>
                    {t(filteredService.name)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <FormLabel htmlFor="description" label={t("Description")} />
          <div className="mt-1">
            <textarea
              value={description}
              onChange={(event) => {
                setdescription(event.target.value);
              }}
              id="description"
              name="description"
              rows="3"
              disabled={isConsultant}
              className={classNames(
                "shadow-sm focus:outline-none focus:ring-0 mt-1 block w-full text-sm border border-gray-300 rounded-md",
                isConsultant ? "bg-gray-100 cursor-not-allowed opacity-60" : "",
              )}
              placeholder={t("Please describe your tax problem")}
            ></textarea>
          </div>
        </div>

        {Boolean(consultationDuration) && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("Consultation Duration")}
            </label>
            {statusLoading ? (
              <div className="flex animate-pulse justify-between mt-1 py-2 px-3 text-gray-700 rounded-md" style={{ backgroundColor: theme.primaryLight }}>
                <div className="flex flex-col">
                  <div className="h-3 mt-[2px] mb-[2px] w-16 bg-gray-300 rounded-md"></div>
                  <div className="h-3 mt-[2px] mb-[2px] w-32 bg-gray-300 rounded-md"></div>
                </div>
              </div>
            ) : (
              <div>
                <div
                  onClick={() => setshowTermModal(true)}
                  className="flex items-center justify-between mt-1 py-2 px-3 text-gray-700 rounded-md cursor-pointer"
                  style={{ backgroundColor: theme.primaryLight }}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium" style={{ color: theme.primary }}>
                      {formatConsultationDuration(consultationDuration)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {t("Look at the Consultation Duration Terms")}
                    </span>
                  </div>
                  <ArrowCircleRightIcon className="h-5 w-5 hidden sm:block" style={{ color: theme.primary }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Out of Hours Information - Using mobile functions only */}
        {consultantLoading || serviceLoading ? (
          <div>
            <label
              htmlFor="consultant"
              className="block text-sm font-medium text-gray-700"
            >
              {t("information")}
            </label>
            <div className="flex animate-pulse justify-between mt-1 py-2 px-3 bg-amber-100 text-gray-700 rounded-md">
              <div className="flex flex-col">
                <div className="h-3 mt-[2px] mb-[2px] w-32 bg-gray-300 rounded-md"></div>
                <div className="h-3 mt-[2px] mb-[2px] w-48 bg-gray-300 rounded-md"></div>
                <div className="h-3 mt-[2px] mb-[2px] w-40 bg-gray-300 rounded-md"></div>
              </div>
            </div>
          </div>
        ) : (
          isOutOfHours && (
            <div>
              <label
                htmlFor="consultant"
                className="block text-sm font-medium text-gray-700"
              >
                {t("information")}
              </label>
              <div className="flex items-center justify-between mt-1 py-2 px-3 bg-amber-100 text-gray-700 rounded-md">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-amber-600">
                    {t("consultationOutsideWorkingHours")}
                  </span>
                  <span className="text-sm text-amber-800">
                    {t("consultationOutsideWorkingHoursMessage")}
                    {(() => {
                      const nextAvailableTime = consultant?.active
                        ? getConsultantNextAvailableTime(consultant, t)
                        : null;

                      return nextAvailableTime ? (
                        <>
                          <br />
                          <br />
                          {t("nextAvailableTime", { time: nextAvailableTime })}
                        </>
                      ) : null;
                    })()}
                  </span>
                </div>
              </div>
            </div>
          )
        )}

        {(audioCallAvailable || videoCallAvailable || zoomCallAvailable) &&
          withAddOns &&
          !guest && (
            <div>
              <FormLabel htmlFor="call-addon" label={t("Call Add-On")} />
              {statusLoading ? (
                <div className="flex flex-col gap-2 mt-1 animate-pulse">
                  <div className="h-20 w-full rounded-lg bg-gray-300"></div>
                  <div className="h-20 w-full rounded-lg bg-gray-300"></div>
                  <div className="h-20 w-full rounded-lg bg-gray-300"></div>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-2 mt-1">
                    {[
                      audioCallAvailable && {
                        key: "audio",
                        title: t("Audio Call"),
                        description: t("callAddon.audioDesc"),
                        icon: MicrophoneIcon,
                        duration: audioCallDuration,
                        price: audioCallPrice,
                        original: audioCallOriginal,
                        useBalance: user.callBalance >= 60,
                        selected: callEnabled && callType === "audio",
                        disabled: error || isConsultant || videoCallBlocked,
                        onClick: () => {
                          if (callEnabled && callType === "audio") {
                            setcallEnabled(false);
                          } else {
                            setzoomEnabled(false);
                            setCallType("audio");
                            setcallEnabled(true);
                          }
                        },
                      },
                      videoCallAvailable && {
                        key: "video",
                        title: t("Video Call"),
                        description: t("callAddon.videoDesc"),
                        icon: VideoCameraIcon,
                        duration: videoCallDuration,
                        price: videoCallPrice,
                        original: videoCallOriginal,
                        useBalance: user.callBalance >= 60,
                        selected: callEnabled && callType === "video",
                        disabled: error || isConsultant || videoCallBlocked,
                        onClick: () => {
                          if (callEnabled && callType === "video") {
                            setcallEnabled(false);
                          } else {
                            setzoomEnabled(false);
                            setCallType("video");
                            setcallEnabled(true);
                          }
                        },
                      },
                      zoomCallAvailable && {
                        key: "zoom",
                        title: t("Zoom Call"),
                        description: t("callAddon.zoomDesc"),
                        icon: DesktopComputerIcon,
                        duration: zoomCallDuration,
                        price: zoomCallPrice,
                        original: zoomCallOriginal,
                        useBalance: false,
                        selected: zoomEnabled,
                        disabled: error || isConsultant,
                        onClick: () => {
                          if (zoomEnabled) {
                            setzoomEnabled(false);
                          } else {
                            setcallEnabled(false);
                            setzoomEnabled(true);
                          }
                        },
                      },
                    ]
                      .filter(Boolean)
                      .map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={option.onClick}
                          disabled={option.disabled}
                          className={classNames(
                            "w-full flex items-start gap-3 p-3 rounded-lg border text-left disabled:cursor-not-allowed disabled:opacity-50",
                            option.selected ? "" : "border-gray-300",
                          )}
                          style={
                            option.selected
                              ? {
                                  borderColor: theme.primary,
                                  backgroundColor: theme.primaryLight,
                                }
                              : {}
                          }
                        >
                          <option.icon
                            className="h-6 w-6 flex-shrink-0 mt-0.5"
                            style={{ color: theme.primary }}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">
                              {option.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              {option.description}
                              {option.useBalance ||
                              option.duration === 0
                                ? null
                                : ` (${formatAddonDuration(option.duration)})`}
                            </p>
                            {option.useBalance ? (
                              <p className="text-sm font-semibold text-green-600 mt-1">
                                {Math.floor(user.callBalance / 60)}{" "}
                                {t("Minutes Quota")}
                              </p>
                            ) : (
                              <p className="text-sm font-semibold text-green-600 mt-1">
                                {option.original > option.price && (
                                  <span className="line-through font-normal text-gray-400 mr-1">
                                    <NumericFormat
                                      displayType={"text"}
                                      value={option.original}
                                      thousandSeparator={"."}
                                      decimalSeparator={","}
                                      prefix={"Rp"}
                                    />
                                  </span>
                                )}
                                <NumericFormat
                                  displayType={"text"}
                                  value={option.price}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                  </div>
                  {videoCallBlocked && (
                    <p className="text-sm text-amber-600 mt-1">
                      {t("ongoingGlobalCallWarning")}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

        {(zoomEnabled || callEnabled) && withAddOns && !guest && (
          <div>
            <FormLabel htmlFor="phone" label={t("Phone")} />
            <FormPhoneInput
              name="phone"
              id="phone"
              disabled={isConsultant}
              className={classNames(
                "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 sm:text-sm",
                isConsultant ? "bg-gray-100 cursor-not-allowed opacity-60" : "",
              )}
              placeholder={t("Enter Phone Number")}
              value={phone}
              onChange={setphone}
            />
          </div>
        )}

        {guest && !user.id && !validatedEmail && (
          <div>
            <FormLabel htmlFor="email" label={t("Email")} required />
            <input
              type="email"
              id="email"
              disabled={isConsultant}
              className={classNames(
                "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 sm:text-sm",
                isConsultant ? "bg-gray-100 cursor-not-allowed opacity-60" : "",
              )}
              placeholder={t("Enter email")}
              value={email}
              onChange={(event) => {
                setemail(event.target.value);
              }}
              required
            />
          </div>
        )}

        {guest && (
          <div>
            <FormLabel htmlFor="voucher" label={t("Voucher Code")} required />
            <div className="relative mt-1">
              <input
                type="text"
                id="voucher"
                disabled={guestVoucherApplied || isConsultant}
                className={classNames(
                  "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 sm:text-sm",
                  guestVoucherApplied || isConsultant
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                    : "",
                )}
                placeholder={t("Enter voucher code")}
                value={voucher}
                onChange={(event) => {
                  setvoucher(event.target.value);
                }}
                required
              />
              {guestVoucherApplied ? (
                <div className="absolute inset-y-0 inline-flex items-center right-4">
                  <button onClick={resetVoucher}>
                    <XIcon className="h-5 w-5 text-slate-600" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={voucher.length === 0}
                  onClick={() => {
                    verifyVoucher(checkoutDispatch, {
                      code: voucher,
                      productType: "consultation-trial",
                    });
                  }}
                  className="absolute inset-y-0 inline-flex items-center right-4"
                >
                  {guestVoucherLoading ? (
                    <svg
                      role="status"
                      className="w-5 h-5 animate-spin text-slate-300"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <span
                      className="text-sm"
                      style={{ color: voucher.length === 0 ? "#D1D5DB" : theme.primary }}
                    >
                      {t("Apply")}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {consultantLoading ? (
          <div>
            <FormLabel htmlFor="consultant" label={t("Consultant")} required />
            <div className="flex flex-col animate-pulse mt-1 py-2 px-3 border border-gray-300 text-gray-700 rounded-md gap-2">
              <div className="h-6 w-full rounded-md bg-gray-300"></div>
              <div className="h-6 w-full rounded-md bg-gray-300"></div>
            </div>
          </div>
        ) : (
          <div>
            {withConsultantButton && !guest && (
              <div>
                {consultant?.id ? (
                  <div>
                    <FormLabel
                      htmlFor="consultant"
                      label={t("Consultant")}
                      required
                    />
                    <div className="flex flex-col mt-1 py-2 px-3 border border-gray-300 text-gray-700 rounded-md">
                      <span className="text-sm font-medium text-gray-900">
                        {consultant.user.name}
                      </span>
                      {consultant.active ? (
                        <span className="text-sm text-gray-500">
                          {experienceYear} {t("Years of Experience")}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">
                          {t("Not Available")}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="consultant"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Consultant")}
                    </label>
                    <div className="flex items-center justify-between mt-1 p-3 border text-sm text-gray-700 rounded-xl" style={{ borderColor: theme.accent, backgroundColor: theme.primaryLight }}>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <span className="flex font-medium text-gray-900 gap-1">
                            {t("Recommended Consultant")}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 mt-0.5">
                          <span>PajakInd</span>
                          <ShieldCheckIcon
                            className="h-4 w-4"
                            style={{ color: theme.primary }}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <a
                        href="/consultant"
                        className="font-medium transition px-4 py-2 rounded-full flex items-center shadow-sm"
                        style={{ backgroundColor: theme.primary, color: theme.primaryText }}
                      >
                        {t("common.change")}
                        <ChevronRightIcon
                          className="ml-1 h-4 w-4"
                          aria-hidden="true"
                        />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {errorMessage && (
          /* Default Alert for all errors */
          <Alert
            type="error"
            message={errorMessage}
            renderButton={() => {
              return (
                <div className="ml-auto pl-3">
                  <div className="-mx-1.5 -my-1.5">
                    <button
                      onClick={() => seterrorMessage("")}
                      type="button"
                      className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                    >
                      <span className="sr-only">Dismiss</span>
                      <XIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>

      <div className="p-4 flex justify-between items-center bg-gray-50 rounded-b-lg">
        <div>
          {!guest && (
            <div>
              <p className="text-xs text-slate-500">{t("Subtotal")}</p>
              {statusLoading || subscriptionLoading ? (
                <div className="animate-pulse h-3 mt-[2px] mb-[2px] w-16 bg-gray-300 rounded-md"></div>
              ) : (
                <p className="text-xs font-medium">
                  {subtotal === 0 ? (
                    <span className="text-xs text-gray-900">{t("Free")}</span>
                  ) : (
                    <NumericFormat
                      displayType={"text"}
                      value={subtotal}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  )}
                </p>
              )}
            </div>
          )}
          {guest && guestVoucherApplied && (
            <div>
              <p className="text-xs text-slate-500">{t("Subtotal")}</p>
              <p className="text-xs font-medium">
                {subtotal === 0 ? (
                  <span className="text-xs text-gray-900">{t("Free")}</span>
                ) : (
                  <NumericFormat
                    displayType={"text"}
                    value={subtotal}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    prefix={"Rp"}
                  />
                )}
              </p>
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={isDisabled}
          className="inline-flex justify-center items-center whitespace-nowrap px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          style={isDisabled ? {} : { backgroundColor: theme.primary, color: theme.primaryText }}
          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = theme.primaryHover; }}
          onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = theme.primary; }}
        >
          {guestAuthLoading ? (
            <svg role="status" className="w-5 h-5 animate-spin text-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
            </svg>
          ) : (
            !consultant?.active && consultant?.id ? t("Not Available") : t("Continue")
          )}
        </button>
      </div>
    </form>
  );
};
